// lib/cockpitBoard.ts — H1a (COCKPIT-HOME-COLUMNS-SPEC.md §H1). Pure
// functions over the column registry (`GET /api/cockpit/columns`) and the
// data `/api/today` already serves — cards, needsYou sort (with its
// reason), and the empty-column statement. No component here; CockpitHome
// is the only renderer, same "lib holds the vocabulary, the screen renders
// it" split lib/proposals.ts already follows for Today/Review.
import type { CockpitColumn, SystemsSummaryApi, TodayBriefing, TodaySystemFact } from './api';
import { proposalKindLabel, proposalSummaryLine } from './proposals';
import { meterSentence } from './meterSentence';

/** H1b (COCKPIT-HOME-COLUMNS-SPEC.md §H1, the Acts table) — what a card
 * IS, for the purpose of which acts it carries. Distinct from a column's
 * `topCard` source: two cards from the same source can differ in kind
 * (a waitingOnMe thread vs. an arrived one). */
export type BoardCardKind =
  | 'proposal'
  | 'proposal_open_only'
  | 'reconnect'
  | 'thread_waiting'
  | 'thread_arrived'
  | 'commitment'
  | 'programme_run'
  | 'programme'
  | 'fact'
  | 'calendar'
  | 'audience'
  // H3a — a project's own status card ("projects/<slug>"). Same floor as
  // `fact` (Open only) today: the Acts table's "plus the acts the fact
  // carries (e.g. Install for a build with a file link)" is deferred —
  // there is no reporter yet (H3's own words, "nothing new on the server
  // for the first project chunk") to populate a fact that could carry
  // one, so there is nothing yet to exercise. Reported as a judgement
  // call rather than built speculatively.
  | 'project_fact';

/** The Acts table's own vocabulary — never send/pay/delete/publish (the
 * brief's own rule, asserted by this file's tests). `open` is the
 * universal act every card already carries via its `link` (H1a); the rest
 * are the ones this chunk adds. */
export type CardActKind = 'approve_proposal' | 'not_now' | 'reconnect' | 'open' | 'done_for_now' | 'approve_programme';

/** Card kind -> its exact act set (COCKPIT-HOME-COLUMNS-SPEC.md §H1 Acts
 * table, H1B-BOARD-ACTS-BRIEF.md's Build table). A kind this file hasn't
 * named carries `open` only — the same "every card can Open" floor H1a
 * already renders. */
const ACTS_BY_KIND: Readonly<Record<BoardCardKind, readonly CardActKind[]>> = {
  proposal: ['approve_proposal', 'not_now'],
  // H1b review round 2 (atlas-core, 2026-10-16): a proposal whose
  // target_type is NOT on SAFE_APPROVABLE_PROPOSAL_TARGET_TYPES below —
  // Open only, no Approve on the board at all. Its own screen (Review)
  // carries whatever gate that kind needs.
  proposal_open_only: ['open'],
  reconnect: ['reconnect'],
  thread_waiting: ['open', 'done_for_now'],
  thread_arrived: ['open'],
  commitment: ['open'],
  programme_run: ['approve_programme', 'open'],
  programme: ['open'],
  fact: ['open'],
  calendar: ['open'],
  audience: ['open'],
  project_fact: ['open'],
};

export function actsForKind(kind: BoardCardKind): readonly CardActKind[] {
  return ACTS_BY_KIND[kind] ?? ['open'];
}

/** H1B-BOARD-ACTS-BRIEF.md, Build: "No act on the board ever sends, pays,
 * deletes or publishes." Exported so the test asserts the CATALOGUE
 * itself (every kind, every act) rather than a hand-picked sample. */
export const ACT_FORBIDDEN_WORDS = ['send', 'pay', 'delete', 'publish'] as const;

/**
 * H1b review round 2 (atlas-core, 2026-10-16) — THE SAFETY GATE ON
 * PRESS-AND-HOLD APPROVE.
 *
 * `approveProposal` is not one action — `POST /api/proposals/:id/approve`
 * (packages/server/src/plugins/proposals.ts) dispatches on the proposal's
 * OWN `targetType` before it ever reaches core's `approveProposal`, and
 * most of those branches send mail, broadcast, or publish:
 *   - `email_draft`         -> applyEmailDraftProposal: SENDS an email (B14)
 *   - `segment_broadcast`   -> applySegmentBroadcastProposal: SENDS a broadcast
 *   - `intake_triage`       -> applyIntakeTriageProposal: REPLIES to a
 *                              stranger (and may start a client)
 *   - `client_letter`       -> applyClientLetterProposal: ships to the
 *                              client's portal AND mails a nudge
 *   - `actor_capture`       -> applyCaptureProposal: replays an arbitrary
 *                              action list through the cascade executor,
 *                              which can fire pack-declared REACTIONS
 *                              (Core gap 22) — not provably side-effect-free
 *   - `post_document`       -> approvePostDocumentProposal: PUBLISHES (PC2)
 *   - `aa_submission_reply` -> applySubmissionReplyProposal: transitions a
 *                              submission to 'invited' — an invite is a
 *                              message to the submitter
 *
 * Only TWO target_types are verified, by reading the actual apply
 * function (not the route's comment), to do NONE of send/pay/delete/
 * publish/message:
 *   - `'commitment'` — the only target_type core's OWN `approveProposal`
 *     (packages/core/src/commitments/proposals.ts) ever materializes.
 *     Either sub-path (`kind: 'commitment_close'` or anything else) ends
 *     in `closeCommitmentFromProposal` or `materializeCommitmentFromProposal`
 *     (packages/core/src/commitments/commitments.ts) — both are a single
 *     SQL UPDATE/INSERT on the `commitments` table, nothing else.
 *   - `'client_work'` — `applyClientWorkProposal`
 *     (packages/core/src/party/clientWork.ts): "unlike the triage card it
 *     sends nothing" (its own header) — verified: `insertTaskInScope` +
 *     `insertClassificationInScope`, both plain writes, no mail/broadcast/
 *     publish call anywhere in the function.
 *
 * AN ALLOWLIST, NEVER A DENYLIST (the review's own rule): a target_type
 * this file has never heard of is UNSAFE by construction — it is simply
 * absent from the Set below, so it falls through to `proposal_open_only`.
 * Widening this set is a safety decision and needs the same reading this
 * comment describes, every time — see the canary in cockpitBoard.test.ts
 * ("the allowlist is a real allowlist, not allow-all").
 */
export const SAFE_APPROVABLE_PROPOSAL_TARGET_TYPES: ReadonlySet<string> = new Set(['commitment', 'client_work']);

export function isProposalSafeToApproveOnBoard(targetType: string): boolean {
  return SAFE_APPROVABLE_PROPOSAL_TARGET_TYPES.has(targetType);
}

export interface BoardCard {
  id: string;
  columnId: string;
  title: string;
  line: string;
  needsYou: boolean;
  kind: BoardCardKind;
  link: string;
  at: string | null;
  /** Act-specific identifiers — only the ones a card's own kind needs are
   * ever set. The screen reads these rather than parsing `id`. */
  proposalId?: string;
  programmeId?: string;
  reconnectId?: string;
  email?: string;
  threadId?: string;
}

/** The few other EXISTING reads the board borrows for a column Today's
 * briefing doesn't cover — never a new card server (the brief's own
 * words). Both are optional: a column whose extra hasn't loaded yet (or
 * never will — Radar/Record have no cheap existing summary) just reads
 * its honest empty state. */
export interface BoardExtras {
  /** Calendar — the single nearest upcoming event, from the SAME
   * `listAgenda` call CockpitCalendar already makes. */
  nextEvent?: { title: string; whenLabel: string; startsAt: string } | null;
  /** Audience — the unanswered tile, from the SAME `getAudienceSummary`
   * call the Audience screen already makes. */
  audienceUnanswered?: number;
  /** H3b — the SAME `getSystemsSummary` read the Systems screen already
   * makes, fetched once by the board alongside Today (CockpitHome.tsx) and
   * handed down here so `findProjectFact` can read the `projects` section
   * off it rather than off Today's `meters.facts`/`proceeding.agents`
   * (H3a's own forward-compatible guess, now superseded — migration 0936
   * widens `system_facts.section` to admit `projects` and a reporter can
   * finally write one). Absent while the read is still in flight, same
   * posture `nextEvent`/`audienceUnanswered` already keep. */
  systemsSummary?: SystemsSummaryApi;
}

function cardFromThread(columnId: string, t: TodayBriefing['arrived'][number], needsYou: boolean): BoardCard {
  return {
    id: `${columnId}:thread:${t.threadId}`,
    columnId,
    title: t.subject ?? '(no subject)',
    line: t.party?.displayName ?? '',
    needsYou,
    kind: needsYou ? 'thread_waiting' : 'thread_arrived',
    threadId: t.threadId,
    link: `/threads/detail/${t.threadId}`,
    at: t.lastMessageAt,
  };
}

function cardFromCommitment(columnId: string, c: TodayBriefing['waitingOnMe']['commitments'][number], needsYou: boolean): BoardCard {
  return {
    id: `${columnId}:commitment:${c.id}`,
    columnId,
    title: c.title,
    line: c.counterpartyName ?? '',
    needsYou,
    kind: 'commitment',
    link: '/delivery',
    at: c.dueAt,
  };
}

function cardFromProposal(columnId: string, p: TodayBriefing['judgment']['proposals'][number]): BoardCard {
  // H1b review round 2 — a proposal whose approval would send/pay/
  // delete/publish/message never gets Hold to approve on the board; it
  // opens Review, where that kind's own gate lives.
  const safe = isProposalSafeToApproveOnBoard(p.targetType);
  return {
    id: `${columnId}:proposal:${p.id}`,
    columnId,
    title: proposalSummaryLine(p),
    line: proposalKindLabel(p.targetType),
    needsYou: true,
    kind: safe ? 'proposal' : 'proposal_open_only',
    ...(safe ? { proposalId: p.id } : {}),
    link: '/review',
    at: p.createdAt,
  };
}

function cardFromRun(columnId: string, r: TodayBriefing['judgment']['runs'][number]): BoardCard {
  return {
    id: `${columnId}:run:${r.id}`,
    columnId,
    title: r.title,
    line: `${r.programmeTitle} · ${r.why}`,
    needsYou: true,
    kind: 'programme_run',
    programmeId: r.programmeId,
    link: r.link,
    at: r.dueAt,
  };
}

function cardFromProgramme(columnId: string, p: TodayBriefing['proceeding']['programmes'][number]): BoardCard {
  return {
    id: `${columnId}:programme:${p.id}`,
    columnId,
    title: p.title,
    line: p.nextRun ? `next: ${p.nextRun.title}` : 'not yet started',
    needsYou: false,
    kind: 'programme',
    link: p.link,
    at: p.nextRun?.dueAt ?? null,
  };
}

function cardFromFact(columnId: string, f: TodaySystemFact): BoardCard {
  return {
    id: `${columnId}:fact:${f.section}:${f.key}`,
    columnId,
    title: f.label,
    line: meterSentence(f),
    needsYou: f.meter === 'at_cap' || f.state === 'needs_you',
    kind: 'fact',
    link: f.link ?? '/systems',
    at: f.asOf,
  };
}

/** H3b — the ONE project status fact a project column's top card reads
 * (COCKPIT-HOME-COLUMNS-SPEC.md §H3: "systems facts section:'projects',
 * key:<slug>"). Migration 0936 widens `system_facts.section` to admit
 * 'projects', so this now reads the REAL `projects` section of the systems
 * summary (`GET /api/systems/summary`) rather than guessing at it inside
 * Today's `meters.facts`/`proceeding.agents` the way H3a's own version of
 * this function did (that CHECK admitted no such row, so the guess was
 * always empty). The board (CockpitHome.tsx) fetches the summary once,
 * alongside Today, and hands it down as `BoardExtras.systemsSummary`;
 * `ProjectScreen.tsx` fetches its own copy the same way. `summary`
 * undefined (the read still in flight, or this slug's own project has no
 * fact yet) reads as "no fact" — the honest empty state, never an error. */
export function findProjectFact(summary: SystemsSummaryApi | undefined, slug: string): TodaySystemFact | undefined {
  return summary?.sections.projects.find((f) => f.key === slug);
}

/** THE PROJECTS CONTRACT (coordinator, round 2, 2026-10-16): a
 * `projects/<slug>` reporter posts state **'up'** (healthy) or **'down'**
 * (health check failed / migrations not current / unreachable) for every
 * live project. Studio Assist (coordinator scope addition, same day) is
 * the one exception while it has nothing deployed: its reporter posts
 * **'pending'** instead, a real `SYSTEM_FACT_STATE` (Core's own enum) that
 * is neither `'down'` nor stale, so it renders QUIET on the board by the
 * SAME rule below — no third branch needed. `needsYou` glows ONLY on
 * `'down'` or on staleness itself — never
 * on `detail`, which is free text a reporter writes for a person to read,
 * not a signal this card computes anything from (pinned by
 * cockpitBoard.test.ts's own canary, "needsYou only through state or
 * staleness"). This is deliberately a NARROWER rule than `cardFromFact`'s
 * own `meter === 'at_cap' || state === 'needs_you'` just above — that
 * function still serves agents/meters/fleet/doors/compliance/recovery,
 * sections this contract never touches; `'needs_you'` was never a real
 * `SYSTEM_FACT_STATE` for projects either (Core's own enum,
 * packages/core/src/systems/types.ts, admits no such value — H3b's first
 * round left this checking for it anyway, a dead comparison this round
 * removes). */
function cardFromProjectFact(column: CockpitColumn, f: TodaySystemFact): BoardCard {
  return {
    id: `${column.id}:project-fact:${f.key}`,
    columnId: column.id,
    // Plain, not "Open <Project>" — BoardCardView's own round-Open button
    // already renders `aria-label="Open " + title`; prefixing it here
    // would read "Open Open Rexx". The Acts table's literal "Open
    // <Project>" wording is this floor (every other card kind's "Open"
    // act names its own card the same generic way).
    title: column.title,
    // Stale always says so — the SAME sentence regardless of whatever
    // free-text `detail` a reporter sent, so a stale fact is never read
    // as current just because its own words still look fresh.
    line: f.stale ? meterSentence(f) : f.detail ?? meterSentence(f),
    needsYou: f.state === 'down' || f.stale,
    kind: 'project_fact',
    // The project's OWN screen, never '/systems' — a project column's
    // card always opens the project, same as every other project act.
    link: f.link ?? column.screen,
    at: f.asOf,
  };
}

function cardFromReconnect(columnId: string, c: { id: string; email: string; reason: string | null }): BoardCard {
  return {
    id: `${columnId}:reconnect:${c.id}`,
    columnId,
    title: `Reconnect ${c.email}`,
    line: c.reason ?? 'Google stopped accepting this connection',
    needsYou: true,
    kind: 'reconnect',
    reconnectId: c.id,
    email: c.email,
    link: '/personal/accounts',
    at: null,
  };
}

/** The column's own queue — every card its `topCard` source produces,
 * oldest rule first (never re-sorted here; `needsYou` sort is a COLUMN
 * property, computed separately by `sortColumns`). */
export function cardsForColumn(column: CockpitColumn, b: TodayBriefing, extra: BoardExtras = {}): BoardCard[] {
  const tc = column.topCard;
  switch (tc.source) {
    case 'today':
      switch (tc.group) {
        case 'arrived':
          return b.arrived.map((t) => cardFromThread(column.id, t, false));
        case 'waitingOnMe':
          return [
            ...b.waitingOnMe.commitments.map((c) => cardFromCommitment(column.id, c, true)),
            ...b.waitingOnMe.threads.map((t) => cardFromThread(column.id, t, true)),
          ];
        case 'judgment':
          return [
            ...b.judgment.proposals.map((p) => cardFromProposal(column.id, p)),
            ...b.judgment.runs.map((r) => cardFromRun(column.id, r)),
          ];
        case 'proceeding':
          return [
            ...b.proceeding.programmes.map((p) => cardFromProgramme(column.id, p)),
            ...b.proceeding.agents.map((f) => cardFromFact(column.id, f)),
          ];
        case 'meters':
          return b.meters.facts.map((f) => cardFromFact(column.id, f));
        case 'reconnect':
          return (b.reconnect ?? []).map((c) => cardFromReconnect(column.id, c));
        default:
          return [];
      }
    case 'calendar':
      return extra.nextEvent
        ? [
            {
              id: `${column.id}:next:${extra.nextEvent.startsAt}`,
              columnId: column.id,
              title: extra.nextEvent.title,
              line: extra.nextEvent.whenLabel,
              needsYou: false,
              kind: 'calendar',
              link: '/calendar',
              at: extra.nextEvent.startsAt,
            },
          ]
        : [];
    case 'audience': {
      const n = extra.audienceUnanswered ?? 0;
      return n > 0
        ? [
            {
              id: `${column.id}:unanswered`,
              columnId: column.id,
              title: n === 1 ? '1 unanswered' : `${n} unanswered`,
              line: 'in the Audience inbox',
              needsYou: true,
              kind: 'audience',
              link: '/audience/inbox',
              at: null,
            },
          ]
        : [];
    }
    // H3b — a project column's own status card, read off the systems
    // summary `extra.systemsSummary` carries (`findProjectFact`'s own
    // header). The personal `Systems` column (above) is `today`/`meters`-
    // sourced, not `systems`-sourced — this arm is project columns ONLY.
    case 'systems': {
      if (tc.section !== 'projects' || !tc.key) return [];
      const f = findProjectFact(extra.systemsSummary, tc.key);
      return f ? [cardFromProjectFact(column, f)] : [];
    }
    // radar/record carry no existing summary read cheap enough to borrow
    // without building one — their column is honestly empty until a
    // later chunk feeds it (reported as a judgement call).
    case 'radar':
    case 'record':
    default:
      return [];
  }
}

/** One line naming what a column tracks, for its empty state ("Nothing
 * needs you." + this — the Today empty-state rule, voice canon §2.5: no
 * instructional caption, name what appears). Keyed by column id so a
 * later project column (H3a) can add its own without touching this file's
 * callers. */
const COLUMN_EMPTY_STATEMENT: Readonly<Record<string, string>> = {
  mail: 'When a message needs a reply, it appears here.',
  calendar: 'Your next meeting appears here.',
  review: 'A proposal waiting on your judgment appears here.',
  delivery: 'A programme or an agent proceeding on your behalf appears here.',
  audience: 'An unanswered comment or message appears here.',
  systems: 'A meter at its cap, or an agent that needs you, appears here.',
  radar: 'A topic worth your attention appears here.',
  record: 'A file Atlas generated on your behalf appears here.',
  // H3a — the project columns. 'project:studio' (Studio Assist) is H4a's
  // own addition (coordinator scope addition, 2026-10-16, Huan's yes).
  'project:rexx': "Rexx's own status appears here.",
  'project:stays': "Studio Stays' own status appears here.",
  'project:voicestrip': "VoiceStrip's own status appears here.",
  'project:origincare': "Origin Care's own status appears here.",
  'project:studio': "Studio Assist's own status appears here.",
};

export function emptyStatementFor(column: CockpitColumn): string {
  return COLUMN_EMPTY_STATEMENT[column.id] ?? 'What this column tracks will appear here.';
}

/** §H1's own sort: columns whose top card `needsYou` first, stable by
 * `pinnedOrder` then title. `cardsByColumn` is read rather than recomputed
 * so the screen's one pass over `cardsForColumn` is the only one. */
export function sortColumns(columns: CockpitColumn[], cardsByColumn: ReadonlyMap<string, BoardCard[]>): CockpitColumn[] {
  const withIndex = columns.map((c, i) => ({
    c,
    i,
    needsYou: (cardsByColumn.get(c.id) ?? []).some((card) => card.needsYou),
  }));
  withIndex.sort((a, b) => {
    if (a.needsYou !== b.needsYou) return a.needsYou ? -1 : 1;
    const ap = a.c.pinnedOrder;
    const bp = b.c.pinnedOrder;
    if (ap !== bp) {
      if (ap === null) return 1;
      if (bp === null) return -1;
      if (ap !== bp) return ap - bp;
    }
    const byTitle = a.c.title.localeCompare(b.c.title);
    return byTitle !== 0 ? byTitle : a.i - b.i;
  });
  return withIndex.map((x) => x.c);
}

/** The long-press/right-click reason ("first because: 2 need you") — null
 * for a column the sort didn't move to the front. */
export function sortReason(column: CockpitColumn, columns: CockpitColumn[], cardsByColumn: ReadonlyMap<string, BoardCard[]>): string | null {
  const needsYouIds = columns.filter((c) => (cardsByColumn.get(c.id) ?? []).some((card) => card.needsYou)).map((c) => c.id);
  if (!needsYouIds.includes(column.id)) return null;
  const n = needsYouIds.length;
  return `first because: ${n} need${n === 1 ? 's' : ''} you`;
}
